import { ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import type { CSSProperties, ReactNode } from "react";

import { ApiError } from "../../../lib/api.js";
import { GTCC_NOTI_KEY, fetchGtccNoti, type GtccPushHealth } from "../../api/noti.js";
import {
  ControlDot,
  ControlFreshBadge,
  ControlHead,
  ControlMono,
  ControlPanel,
  ControlRow,
  ControlTableWrap,
  HOST_CELL_STYLE,
  TABLE_STYLE,
  TD_STYLE,
  TH_STYLE,
  formatInteger,
  type ControlTone,
} from "../control/ControlPrimitives.js";

const MONO_CELL: CSSProperties = {
  ...TD_STYLE,
  fontFamily: "var(--gt-sys-font-family-mono)",
};

interface EventRow {
  readonly kind: string;
  readonly label: string;
  readonly status: string;
  readonly tone: ControlTone;
}

function eventLabel(kind: string, t: (key: string) => string): string {
  if (kind === "alarm.geofence") return t("admin.gtcc.noti.control.event.alarm.geofence");
  if (kind === "maintenance.due") return t("admin.gtcc.noti.control.event.maintenance.due");
  return kind;
}

function eventRows(
  data: GtccPushHealth | undefined,
  t: (key: string) => string,
): readonly EventRow[] {
  const deliverableKinds = Array.isArray(data?.deliverableKinds) ? data.deliverableKinds : [];
  return [...deliverableKinds].sort().map((kind) => {
    const available = kind !== "maintenance.due" || data?.maintenanceDueEnabled;
    return available
      ? {
          kind,
          label: eventLabel(kind, t),
          status: t("admin.gtcc.noti.control.status.active"),
          tone: "ok",
        }
      : {
          kind,
          label: eventLabel(kind, t),
          status: t("admin.gtcc.noti.control.status.push_flag_off"),
          tone: "info",
        };
  });
}

/** Reference sheet lines 821–835, backed only by GET push-health counts and gates. */
export function NotiView(): ReactNode {
  const { t, locale } = useLocale();
  const query = useQuery({
    queryKey: GTCC_NOTI_KEY,
    queryFn: ({ signal }) => fetchGtccNoti(signal),
    staleTime: 30_000,
  });
  const freshnessSeconds = query.data?.freshnessSeconds;
  const head = (
    <ControlHead
      testId="gtcc-noti-head"
      title="Notifications"
      subtitle={t("admin.gtcc.noti.control.subtitle")}
      fresh={
        <ControlFreshBadge
          testId="gtcc-noti-fresh"
          text={
            typeof freshnessSeconds === "number"
              ? t("admin.gtcc.cockpit.infra.fresh.seconds", {
                  seconds: formatInteger(freshnessSeconds, locale),
                })
              : t("admin.gtcc.noti.control.fresh")
          }
        />
      }
    />
  );
  if (query.isPending)
    return (
      <Stack gap={3} data-testid="gtcc-view-noti">
        {head}
        <LoadingState label={t("admin.gtcc.noti.control.loading")} />
      </Stack>
    );
  if (query.isError)
    return (
      <Stack gap={3} data-testid="gtcc-view-noti">
        {head}
        <ErrorState
          severity="warn"
          title={t(
            query.error instanceof ApiError && query.error.status === 403
              ? "admin.gtcc.noti.forbidden.title"
              : "admin.gtcc.noti.error.title",
          )}
        />
      </Stack>
    );

  const rows = eventRows(query.data, t);
  const deliveries7d = query.data?.deliveries7d;
  const delivered = typeof deliveries7d?.total === "number" ? deliveries7d.total : 0;
  const failures = typeof deliveries7d?.failed === "number" ? deliveries7d.failed : 0;
  const activePushTokens = query.data?.activePushTokens?.value;
  const hasActivePushTokens = typeof activePushTokens === "number" && activePushTokens >= 0;
  return (
    <Stack gap={3} data-testid="gtcc-view-noti">
      {head}
      <ControlPanel testId="gtcc-noti-catalog" title={t("admin.gtcc.noti.control.catalog")}>
        <ControlTableWrap testId="gtcc-noti-table">
          <table aria-label={t("admin.gtcc.noti.control.catalog")} style={TABLE_STYLE}>
            <thead>
              <tr>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.noti.control.column.event")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.noti.control.column.in_app")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.noti.control.column.telegram")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.noti.control.column.status")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4}>{t("admin.gtcc.noti.empty.title")}</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.kind}>
                    <td style={HOST_CELL_STYLE}>{row.label}</td>
                    <td style={MONO_CELL}>✓</td>
                    <td style={MONO_CELL}>✓</td>
                    <td style={TD_STYLE}>
                      <span
                        style={{
                          display: "inline-flex",
                          gap: "var(--gt-sys-space-1)",
                          alignItems: "center",
                        }}
                      >
                        <ControlDot tone={row.tone} />
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ControlTableWrap>
      </ControlPanel>
      <ControlPanel testId="gtcc-noti-health" title={t("admin.gtcc.noti.control.health")}>
        <ControlRow
          tone={hasActivePushTokens ? "ok" : "info"}
          label={t("admin.gtcc.noti.control.active_tokens")}
          value={<ControlMono>{hasActivePushTokens ? activePushTokens : "—"}</ControlMono>}
        />
        <ControlRow
          tone={failures > 0 ? "warn" : "ok"}
          label={t("admin.gtcc.noti.control.delivered_7d")}
          value={
            <ControlMono>
              {t("admin.gtcc.noti.control.delivery_value", { delivered, failures })}
            </ControlMono>
          }
        />
      </ControlPanel>
    </Stack>
  );
}
